import React, { useState } from "react";
import { Box, Stack, Modal, IconButton, styled, Skeleton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";

// Define our prop types for better type safety
interface ImageGridProps {
  images: string[];
  onImagePress?: (index: number, image: string) => void;
}

// Create styled components for consistent styling
const ModalContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90vw",
  maxHeight: "90vh",
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[24],
  padding: theme.spacing(4),
  overflow: "hidden",
}));

const ImageContainer = styled(Box)({
  cursor: "pointer",
  overflow: "hidden",
  position: "relative",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform 0.3s ease",
  },
  "&:hover img": {
    transform: "scale(1.05)",
  },
});

const ImageGrid: React.FC<ImageGridProps> = ({ images, onImagePress }) => {
  const theme = useTheme();
  const [imageError, setImageError] = useState(false);
  const [imageStates, setImageStates] = useState<boolean[]>(
    images.map(() => true)
  );
  const [imageSources, setImageSources] = useState<string[]>(images);

  // State for modal control
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Handler for image clicks
  const handleImageClick = (index: number, image: string) => {
    setSelectedImageIndex(index);
    setModalOpen(true);
    onImagePress?.(index, image);
  };

  // Render single image layout
  const renderSingleImage = () => (
    <ImageContainer
      sx={{
        width: "100%",
        height: "240px",
        m: "6px",
        borderRadius: "6px",
      }}
      onClick={() => handleImageClick(0, images[0])}
    >
      {imageError ? (
        <Skeleton height="100%" />
      ) : (
        <img
          src={images[0]}
          alt="Single view"
          onError={() => setImageError(true)}
        />
      )}
    </ImageContainer>
  );

  // Render two images layout
  const renderTwoImages = () => (
    <Stack direction="row" sx={{ width: "100%", height: "190px" }}>
      {images.map((image, index) => (
        <ImageContainer
          key={index}
          sx={{
            width: "49%",
            m: "6px",
            borderRadius: "8px",
            position: "relative",
          }}
          onClick={() => handleImageClick(index, image)}
        >
          {imageStates[index] && (
            <Skeleton variant="rectangular" height="100%" />
          )}
          <img
            src={imageSources[index]}
            alt={`Image ${index + 1}`}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: imageStates[index] ? "none" : "block",
            }}
            onLoad={() => {
              setImageStates((prev) => {
                const newStates = [...prev];
                newStates[index] = false;
                return newStates;
              });
            }}
            onError={() => {
              // Set fallback image source
              setImageSources((prev) => {
                const newSources = [...prev];
                newSources[index] = "/assets/images/home/irep-image.png";
                return newSources;
              });
              // Keep showing skeleton until fallback loads
              setImageStates((prev) => {
                const newStates = [...prev];
                newStates[index] = true;
                return newStates;
              });
            }}
          />
        </ImageContainer>
      ))}
    </Stack>
  );

  // Render three images layout
  const renderThreeImages = () => (
    <Box sx={{ width: "100%", height: "440px" }}>
      <ImageContainer
        sx={{
          m: "6px",
          borderRadius: "8px",
          width: "100%",
          height: "250px",
        }}
        onClick={() => handleImageClick(0, images[0])}
      >
        <img src={images[0]} alt="Main view" />
      </ImageContainer>
      <Stack direction="row" sx={{ width: "100%", height: "190px" }}>
        {images.slice(1).map((image, index) => (
          <ImageContainer
            key={index}
            sx={{
              width: "49%",
              m: "6px",
              borderRadius: "8px",
            }}
            onClick={() => handleImageClick(index + 1, image)}
          >
            <img src={image} alt={`Image ${index + 2}`} />
          </ImageContainer>
        ))}
      </Stack>
    </Box>
  );

  // Render four or more images layout
  const renderManyImages = () => (
    <Stack direction="row" sx={{ width: "100%", height: "190px" }}>
      {images.slice(0, 3).map((image, index) => (
        <ImageContainer
          key={index}
          sx={{
            width: "24%",
            m: "6px",
            borderRadius: "8px",
          }}
          onClick={() => handleImageClick(index, image)}
        >
          <img src={image} alt={`Image ${index + 1}`} />
        </ImageContainer>
      ))}
      <ImageContainer
        sx={{
          width: "24%",
          m: "6px",
          borderRadius: "8px",
          position: "relative",
        }}
        onClick={() => handleImageClick(3, images[3])}
      >
        <img src={images[3]} alt="More images preview" />
        {images.length > 4 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "24px",
              fontWeight: "bold",
            }}
          >
            +{images.length - 4}
          </Box>
        )}
      </ImageContainer>
    </Stack>
  );

  // Modal content with horizontally scrollable images

  const renderModal = () => (
    <Modal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      aria-labelledby="image-modal"
      sx={{
        
      }}
    >
      <ModalContainer style={{
        backgroundColor: "#797979",
      }}>
        <IconButton
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            zIndex: 1,
          }}
          onClick={() => setModalOpen(false)}
        >
          <CloseIcon />
        </IconButton>

        {/* Scrollable container with center alignment */}
        <Box
          sx={{
            mt: 4,
            overflowX: "auto",
            overflowY: "hidden",
            "&::-webkit-scrollbar": {
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "rgba(0,0,0,0.1)",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.2)",
              borderRadius: "4px",
            },
          }}
        >
          {/* Flex container for images with centering */}
          <Stack
            direction="row"
            spacing={2}
            sx={{
              minWidth: "min-content",
              p: 1,
              // Center the stack if content doesn't overflow
              margin: "0 auto",
              justifyContent: {
                xs: "flex-start", // Start alignment on small screens (scrollable)
                md: "center", // Center on larger screens if no overflow
              },
            }}
          >
            {images.map((image, index) => (
              <Box
                key={index}
                sx={{
                  // Fixed dimensions for uniform sizing
                  height: 400,
                  width: 400, // Fixed width for uniformity
                  flexShrink: 0, // Prevent shrinking
                  cursor: "pointer",
                  border:
                    index === selectedImageIndex
                      ? `4px solid ${theme.palette.primary.main}`
                      : "none",
                  borderRadius: 1,
                  overflow: "hidden",
                  // Center the image inside its container
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(0, 0, 0, 0.04)", // Light background for empty spaces
                }}
                onClick={() => setSelectedImageIndex(index)}
              >
                <img
                  src={image}
                  alt={`Full size ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain", // Maintain aspect ratio within bounds
                  }}
                />
              </Box>
            ))}
          </Stack>
        </Box>
      </ModalContainer>
    </Modal>
  );

  // Main render logic remains the same...
  if (!images || images.length === 0) return null;

  return (
    <Box>
      {images.length === 1 && renderSingleImage()}
      {images.length === 2 && renderTwoImages()}
      {images.length === 3 && renderThreeImages()}
      {images.length > 3 && renderManyImages()}
      {renderModal()}
    </Box>
  );
};

export default ImageGrid;
